import React, { useMemo, useCallback } from 'react'

import config from 'constants/config'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { getPlural } from 'lib/string/pluralize'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import setRegionCookie from 'cookies/setRegionCookie'
import { Status, setGeoRedirect } from 'storage/geo'

interface Props {
  checkoutCurrency: string
  mismatchedCurrencies: Array<string>
}

function CurrencyMismatchWarning({ checkoutCurrency, mismatchedCurrencies }: Props) {
  const redirectRegions = useMemo(() => {
    return mismatchedCurrencies.reduce<Record<string, Array<App.RegionSummary>>>((acc, currency) => {
      acc[currency] = config.regions.filter(region => region.currencyCode === currency)
      return acc
    }, {})
  }, [mismatchedCurrencies])

  const onRegionLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button === 0 || e.button === 1) {
      const regionCode = e.currentTarget.dataset.regioncode ?? ''
      setRegionCookie(regionCode)
      setGeoRedirect(Status.redirected)
    }
  }, [])

  return (
    <MessageBanner kind="warning" heading="Currency mismatch" description={
      <VerticalSpacer gap={8}>
        {mismatchedCurrencies.length === 1 && (
          <div>You have redeemed credit in a currency that does not match the current checkout currency (<BodyText variant="medium" weight="bold" as="span">{checkoutCurrency}</BodyText>).</div>
        )}
        {mismatchedCurrencies.length > 1 && (
          <div>You have redeemed credit in currencies that do not match the current checkout currency (<BodyText variant="medium" weight="bold" as="span">{checkoutCurrency}</BodyText>).</div>
        )}
        {mismatchedCurrencies.map(currency => (<>
          <div>Redeemed credit for <BodyText variant="medium" weight="bold" as="span">{currency}</BodyText> will be available in the following {getPlural('region', redirectRegions[currency].length)}:</div>
          <ul>
            {redirectRegions[currency].map(region => (
              <li key={region.code}>
                <TextLink
                  href={`/${region.code.toLowerCase()}`}
                  data-regioncode={region.code}
                  onClick={onRegionLinkClick}
                  onAuxClick={onRegionLinkClick}
                >
                  {region.name}
                </TextLink>
              </li>
            ))}
          </ul>
        </>))}
      </VerticalSpacer>}
    />
  )
}

export default CurrencyMismatchWarning
